import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, Camera, Car, CheckCircle2, Clock, Users } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useAuthStore } from '../store/auth';
import dayjs, { fmtDayLong, fmtTime } from '../lib/dates';
import { InstructorLesson } from '../types';
import { canSignAttendance, lessonState } from '../lib/lessonStatus';
import { isOfferedOpenSlot } from '../lib/openSlot';
import { Button, Card, KpiCard, cx } from '../components/charte';
import { useUiStore } from '../store/ui';
import { useVehicleDamagesEnabled } from '../api/vehicleDamages';
import { LessonCard, OpenSlotCard } from '../components/lesson/LessonCard';
import { NextEventCard } from '../components/lesson/NextEventCard';

// Ma journée (refonte du 14/10/2026, tablette d'abord) : la leçon en cours ou la prochaine en tête, avec son
// action principale ; les chiffres du jour ; puis la frise de la journée, coupée par un repère « Maintenant ».
// J28 (28/10/2026) : plus de leçon à venir aujourd'hui, le prochain événement daté (leçon ou accompagnement à
// l'examen) prend la tête, au lieu de « Aucune leçon prévue aujourd'hui ».

const isReal = (l: InstructorLesson) => !l.isOpenSlot && l.status !== 'cancelled';
/** Leçon finie qui attend encore l'émargement ou le bilan. */
const toFinish = (l: InstructorLesson, now: number) =>
  isReal(l) && !!l.candidateId && Date.parse(l.endsAt) < now && (canSignAttendance(l) || !l.hasBilan);

function dotClass(lesson: InstructorLesson) {
  if (lesson.isOpenSlot) return isOfferedOpenSlot(lesson) ? 'bg-emerald-400 ring-emerald-50' : 'bg-slate-300 ring-slate-100';
  const state = lessonState(lesson);
  if (state === 'honored') return 'bg-success ring-success-light';
  if (state === 'noshow' || state === 'absent') return 'bg-danger ring-danger-light';
  if (state === 'cancelled') return 'bg-slate-300 ring-slate-100';
  return 'bg-primary ring-primary-50';
}

function TimelineRow({ lesson, last }: { lesson: InstructorLesson; last: boolean }) {
  return (
    <li className="flex gap-3 sm:gap-4">
      <div className="w-11 shrink-0 pt-3.5 text-right sm:w-14">
        <p className="text-sm font-semibold tabular-nums text-heading">{fmtTime(lesson.startsAt)}</p>
        <p className="text-xs tabular-nums text-muted">{fmtTime(lesson.endsAt)}</p>
      </div>
      <div className="flex shrink-0 flex-col items-center pt-5" aria-hidden>
        <span className={cx('h-3 w-3 rounded-full ring-4', dotClass(lesson))} />
        {!last && <span className="mt-1 w-px flex-1 bg-line" />}
      </div>
      <div className="min-w-0 flex-1 pb-4">
        {lesson.isOpenSlot ? <OpenSlotCard lesson={lesson} /> : <LessonCard lesson={lesson} />}
      </div>
    </li>
  );
}

function NowMarker({ now }: { now: number }) {
  const time = fmtTime(new Date(now).toISOString());
  return (
    <li className="flex items-center gap-3 pb-4 sm:gap-4" aria-label={`Maintenant, ${time}`}>
      <span className="w-11 shrink-0 text-right text-xs font-semibold tabular-nums text-primary-600 sm:w-14" aria-hidden>
        {time}
      </span>
      <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-primary" aria-hidden />
      <span className="h-px flex-1 bg-primary-200" aria-hidden />
      <span className="text-eyebrow uppercase text-primary-600" aria-hidden>
        Maintenant
      </span>
    </li>
  );
}

export default function TodayView() {
  const user = useAuthStore((s) => s.user);
  const openDamageReport = useUiStore((s) => s.openDamageReport);
  const vehiclesEnabled = useVehicleDamagesEnabled();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['today', user?.id],
    queryFn: instructorApi.getToday,
  });
  // L'heure avance : la leçon en tête et le repère « Maintenant » suivent sans recharger.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  if (isLoading) return <p className="text-secondary">Chargement de votre journée…</p>;
  if (isError)
    return (
      <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
        Impossible de charger votre journée : {(error as Error).message}
      </p>
    );
  if (!data) return null;

  const { stats } = data;
  const lessons = [...data.lessons].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  // Compté à l'écran, comme les cartes : un créneau qui commence pendant que la journée est
  // ouverte n'est plus annoncé ouvert, sans attendre le prochain chargement.
  const openSlotsToday = lessons.filter((l) => isOfferedOpenSlot(l, now)).length;

  const current = lessons.find((l) => isReal(l) && Date.parse(l.startsAt) <= now && Date.parse(l.endsAt) > now);
  const next = lessons.find((l) => isReal(l) && Date.parse(l.startsAt) > now);
  const focus = current ?? next;
  const rest = lessons.filter((l) => l.id !== focus?.id);
  const nowIndex = rest.findIndex((l) => Date.parse(l.startsAt) > now);
  const unfinished = lessons.filter((l) => toFinish(l, now)).length;

  let highlight = '';
  if (current) highlight = `En cours · jusqu'à ${fmtTime(current.endsAt)}`;
  else if (next) {
    const minutes = dayjs(next.startsAt).diff(dayjs(now), 'minute');
    highlight = `À suivre · ${fmtTime(next.startsAt)} – ${fmtTime(next.endsAt)}${minutes < 60 ? ` · dans ${Math.max(minutes, 1)} min` : ''}`;
  }

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-title-md text-heading sm:text-title-lg">Bonjour {user?.firstName ?? ''} 👋</h1>
        <p className="mt-1 text-sm text-secondary">Voici votre journée du {fmtDayLong(data.date)}.</p>
      </header>

      {focus ? (
        <LessonCard lesson={focus} featured highlight={highlight} />
      ) : (
        <>
          {lessons.length > 0 && (
            <Card className="flex items-center gap-4 p-5 sm:p-6">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-surface-indigo text-primary">
                <CheckCircle2 size={22} aria-hidden />
              </span>
              <div>
                <p className="text-[15px] font-semibold text-heading">Plus de leçon à venir aujourd’hui</p>
                <p className="text-sm text-secondary">
                  {unfinished > 0
                    ? `${unfinished} leçon${unfinished > 1 ? 's' : ''} à terminer (émargement ou résumé) dans la journée ci-dessous.`
                    : 'Toutes vos leçons du jour sont terminées.'}
                </p>
              </div>
            </Card>
          )}
          <NextEventCard />
        </>
      )}

      {/* Dégât repéré en prenant le véhicule : photo et signalement avant de partir (24/10/2026). */}
      {vehiclesEnabled && lessons.some(isReal) && (
        <Card className="flex flex-wrap items-center gap-3 p-4 sm:p-5">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
            <Car size={18} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-heading">Un dégât sur le véhicule ?</p>
            <p className="text-[13px] text-secondary">Prenez-le en photo en prenant le véhicule : l’auto-école saura qu’il était déjà là.</p>
          </div>
          <Button size="sm" icon={Camera} onClick={() => openDamageReport()}>
            Signaler un dégât
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <KpiCard
          label="Leçons aujourd'hui"
          icon={CalendarCheck}
          value={stats.lessonsToday}
          foot={
            openSlotsToday > 0
              ? `${openSlotsToday} créneau(x) ouvert(s) à la réservation`
              : stats.lessonsToday > 0
                ? 'séance(s) planifiée(s)'
                : 'Aucune leçon'
          }
        />
        <KpiCard label="Heures ce mois" icon={Clock} value={stats.hoursThisMonth} unit="h" foot="Heures de conduite réalisées" />
        <KpiCard
          label="Élèves suivis"
          icon={Users}
          value={stats.studentsCount}
          foot={stats.readyForExamCount > 0 ? `dont ${stats.readyForExamCount} prêt(s) pour l'examen` : "aucun prêt pour l'examen"}
        />
      </div>

      {rest.length > 0 && (
        <Card className="p-4 sm:p-6">
          <div className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-title-sm text-heading">Ma journée</h2>
            <p className="text-[13px] text-secondary">
              {unfinished > 0 ? `${unfinished} à terminer · ` : ''}Faites le résumé à la fin de chaque séance.
            </p>
          </div>
          <ol>
            {rest.map((lesson, i) => (
              <TimelineFragment key={lesson.id} lesson={lesson} last={i === rest.length - 1} marker={i === nowIndex} now={now} />
            ))}
            {nowIndex === -1 && dayjs(data.date).isSame(dayjs(now), 'day') && <NowMarker now={now} />}
          </ol>
        </Card>
      )}
    </section>
  );
}

function TimelineFragment({ lesson, last, marker, now }: { lesson: InstructorLesson; last: boolean; marker: boolean; now: number }) {
  return (
    <>
      {marker && <NowMarker now={now} />}
      <TimelineRow lesson={lesson} last={last} />
    </>
  );
}
